import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import axiosWrapper from '../../utils/apiRequests/axiosWrapper';
import { updateUserInfo } from '../../features/user/userSlice';
import { useTheme } from '../../contexts/ThemeContext';
import useThemeStyles from '../../hooks/useThemeStyles';
import WritingGoalsCard from './components/WritingGoalsCard';
import WritingActivityCard from './components/WritingActivityCard';
import ProfileCard from './components/ProfileCard';
import PreferencesCard from './components/PreferencesCard';
import urls from '../../urls';

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userInfo);
  const { changeTheme } = useTheme();
  const { colors } = useThemeStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');

  // Preferences state
  const [theme, setTheme] = useState('light');
  const [dailyWordGoal, setDailyWordGoal] = useState(500);

  // Activity state
  const [activity, setActivity] = useState([]);

  // Calculate today's progress
  const todayProgress = useMemo(() => {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const todayActivity = activity.find((entry) => {
      const entryDate = new Date(entry.date);
      const entryKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`;
      return entryKey === todayKey;
    });
    return todayActivity?.wordCount || 0;
  }, [activity]);

  // Calculate weekly progress
  const { weeklyProgress, weeklyTarget, weeklyStatus } = useMemo(() => {
    const weeklyTargetValue = dailyWordGoal * 7;
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    // Get start of week (Sunday)
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    startOfWeek.setUTCHours(0, 0, 0, 0);

    // Get end of week (Saturday)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setUTCHours(23, 59, 59, 999);

    // Calculate total words written this week
    let weeklyTotal = 0;
    activity.forEach((entry) => {
      const entryDate = new Date(entry.date);
      entryDate.setUTCHours(0, 0, 0, 0);
      if (entryDate >= startOfWeek && entryDate <= endOfWeek) {
        weeklyTotal += entry.wordCount || 0;
      }
    });

    // Calculate days elapsed in week (including today)
    const daysElapsed = Math.min(dayOfWeek + 1, 7);
    const expectedProgress = (weeklyTargetValue / 7) * daysElapsed;

    // Determine status
    let status = 'On pace';
    if (weeklyTotal < expectedProgress * 0.9) {
      status = 'Behind';
    } else if (weeklyTotal > expectedProgress * 1.1) {
      status = 'Ahead';
    }

    return {
      weeklyProgress: weeklyTotal,
      weeklyTarget: weeklyTargetValue,
      weeklyStatus: status,
    };
  }, [activity, dailyWordGoal]);

  // Calculate streaks
  const { currentStreak, longestStreak } = useMemo(() => {
    if (!activity || activity.length === 0) {
      return { currentStreak: 0, longestStreak: 0 };
    }

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    // Create a map of activity by date
    const activityMap = new Map();
    activity.forEach((entry) => {
      const date = new Date(entry.date);
      date.setUTCHours(0, 0, 0, 0);
      const dateKey = date.getTime();
      activityMap.set(dateKey, entry.wordCount || 0);
    });

    // Calculate current streak
    let currentStreakCount = 0;
    const todayCount = activityMap.get(todayTime) || 0;
    if (todayCount >= dailyWordGoal) {
      currentStreakCount = 1;
    }

    // Check previous days for current streak
    for (let i = 1; i <= 365; i += 1) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      checkDate.setUTCHours(0, 0, 0, 0);
      const dateKey = checkDate.getTime();
      const wordCount = activityMap.get(dateKey) || 0;

      if (wordCount >= dailyWordGoal) {
        currentStreakCount += 1;
      } else {
        break;
      }
    }

    // Calculate longest streak
    let longestStreakCount = 0;
    let tempStreak = 0;

    // Get all dates from activity, sorted
    const allDates = Array.from(activityMap.keys()).sort((a, b) => b - a);

    // Check each date for streaks
    for (let i = 0; i < allDates.length; i += 1) {
      const wordCount = activityMap.get(allDates[i]);
      if (wordCount >= dailyWordGoal) {
        tempStreak += 1;
        longestStreakCount = Math.max(longestStreakCount, tempStreak);
      } else {
        tempStreak = 0;
      }
    }

    return {
      currentStreak: currentStreakCount,
      longestStreak: longestStreakCount,
    };
  }, [activity, dailyWordGoal]);

  const fetchSettings = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await axiosWrapper({
        method: 'get',
        path: 'users/settings',
      });

      if (fetchError) {
        setError(fetchError.message || 'Failed to fetch settings');
        return;
      }

      if (data) {
        setFullName(data.fullName || '');
        setEmail(data.email || '');
        // Only set theme if it's light or dark (for now)
        const userTheme = data.theme || 'light';
        setTheme(userTheme === 'dark' ? 'dark' : 'light');
        setDailyWordGoal(data.dailyWordGoal || 500);
        setActivity(data.dailyActivity || []);

        // Update theme in context only if it's different
        if (userTheme === 'dark' || userTheme === 'light') {
          changeTheme(userTheme);
        }
      }
    } catch (err) {
      setError('Failed to load settings');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  }, [changeTheme]);

  const settingsUserId = user?._id;
  useEffect(() => {
    // Redirect to home if not logged in
    if (!settingsUserId) {
      navigate(urls.base);
      return;
    }
    fetchSettings();
  }, [settingsUserId, navigate, fetchSettings]);

  const handleUpdateProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data, error: updateError } = await axiosWrapper({
        method: 'put',
        path: 'users/settings/profile',
        data: { fullName, email },
      });

      if (updateError) {
        setError(updateError.message || 'Failed to update profile');
        return;
      }

      if (data?.user) {
        dispatch(updateUserInfo(data.user));
        setSuccess('Profile updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = async (newTheme) => {
    const previousTheme = theme;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Update theme immediately for instant feedback
      changeTheme(newTheme);
      setTheme(newTheme);

      const { error: updateError } = await axiosWrapper({
        method: 'put',
        path: 'users/settings/preferences',
        data: { theme: newTheme, dailyWordGoal },
      });

      if (updateError) {
        setError(updateError.message || 'Failed to update theme');
        // Revert theme change on error
        changeTheme(previousTheme);
        setTheme(previousTheme);
        return;
      }

      // Update Redux store with new theme
      dispatch(updateUserInfo({ theme: newTheme }));

      setSuccess('Theme updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update theme');
      console.error('Error updating theme:', err);
      // Revert theme change on error
      changeTheme(previousTheme);
      setTheme(previousTheme);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { error: updateError } = await axiosWrapper({
        method: 'put',
        path: 'users/settings/preferences',
        data: { theme, dailyWordGoal },
      });

      if (updateError) {
        setError(updateError.message || 'Failed to update preferences');
        return;
      }

      // Update theme immediately
      changeTheme(theme);

      setSuccess('Preferences updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update preferences');
      console.error('Error updating preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        style={{ backgroundColor: colors.background.primary }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      id="settings-page"
      sx={{
        padding: { xs: 2, sm: 3, md: 4 },
        width: '100%',
        backgroundColor: colors.background.primary,
        color: colors.text.primary,
        minHeight: 'calc(100vh - 64px)',
        overflowY: 'auto',
        height: 'calc(100vh - 64px)',
      }}
    >
      <Box id="settings-header" sx={{ marginBottom: 4 }}>
        <Typography
          id="settings-title"
          variant="h4"
          component="h1"
          sx={{ fontWeight: 600, marginBottom: 1 }}
        >
          Account Settings
        </Typography>
        <Typography id="settings-subtitle" variant="body2" sx={{ color: colors.text.secondary }}>
          Manage your writing goals, activity, and preferences
        </Typography>
      </Box>

      {error && (
        <Alert
          id="settings-error-alert"
          severity="error"
          onClose={() => setError(null)}
          sx={{ marginBottom: 2 }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          id="settings-success-alert"
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ marginBottom: 2 }}
        >
          {success}
        </Alert>
      )}

      <Box
        id="settings-content"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        {/* Writing Goals and Activity Section - Side by side on larger screens */}
        <Box
          id="writing-goals-activity-section"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: '100%',
            alignItems: 'flex-start',
          }}
        >
          <WritingGoalsCard
            dailyWordGoal={dailyWordGoal}
            onDailyWordGoalChange={setDailyWordGoal}
            todayProgress={todayProgress}
            weeklyProgress={weeklyProgress}
            weeklyTarget={weeklyTarget}
            weeklyStatus={weeklyStatus}
            currentStreak={currentStreak}
            longestStreak={longestStreak}
            saving={saving}
            onSave={handleUpdatePreferences}
          />
          <WritingActivityCard activity={activity} dailyWordGoal={dailyWordGoal} />
        </Box>

        {/* Profile Section */}
        <ProfileCard
          fullName={fullName}
          onFullNameChange={setFullName}
          email={email}
          onEmailChange={setEmail}
          saving={saving}
          onSave={handleUpdateProfile}
        />

        {/* Preferences Section */}
        <PreferencesCard theme={theme} onThemeChange={handleThemeChange} />
      </Box>
    </Box>
  );
};

export default Settings;
